import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { CountryProvider } from './contexts/CountryContext';
import InstallPrompt from './components/InstallPrompt';
import UpdatePrompt from './components/UpdatePrompt';
import { initPushNotifications } from './utils/pushNotifications';
import { initNativeUI } from './utils/nativeInit';
import { checkForAppUpdate } from './utils/appUpdate';
import { captureReferral } from './utils/referral';
import { initGA, trackPageView } from './utils/analytics';
import { initMetaAppEvents } from './utils/metaAppEvents';
import { markSession } from './utils/engagement';
import ShareAppPrompt from './components/ShareAppPrompt';

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}
import RootRoute from './components/RootRoute';
import Auth from './pages/Auth';
import RequireAuth from './components/RequireAuth';
import AppShell from './components/AppShell';
// Eager: marketing / SEO / prerendered landing pages (LCP-critical, lightweight)
import TaxCalculator from './pages/TaxCalculator';
import GstCalculator from './pages/GstCalculator';
import EmbedCalculator from './pages/EmbedCalculator';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import WhatsAppBookkeeping from './pages/WhatsAppBookkeeping';
import WhatsAppAccounting from './pages/WhatsAppAccounting';
import Demo from './pages/Demo';
import Business from './pages/Business';
import InvoiceScanner from './pages/InvoiceScanner';
import ScanCenterLanding from "./pages/ScanCenterLanding";
import Contact from "./pages/Contact";
import ReceiptScanningForTaxes from "./pages/ReceiptScanningForTaxes";
import SelfEmployedBookkeeping from "./pages/SelfEmployedBookkeeping";
import WhatsAppBookkeepingGuide from "./pages/WhatsAppBookkeepingGuide";
import ForAccountants from './pages/ForAccountants';
import PartnersAccountants from './pages/PartnersAccountants';
import Pricing from './pages/Pricing';
import AppLink from './pages/AppLink';
import AboutMoneyFeed from './pages/AboutMoneyFeed';
import Features from './pages/Features';
import Tools from './pages/Tools';
import Security from './pages/Security';
import SupportedCountries from './pages/SupportedCountries';
import TaxSupport from './pages/TaxSupport';
import Integrations from './pages/Integrations';
import AiBookkeeping from './pages/AiBookkeeping';
import ExpenseTracking from './pages/ExpenseTracking';
import MileageTracking from './pages/MileageTracking';
import FreelancerBookkeeping from './pages/FreelancerBookkeeping';
import Bookkeeping1099 from './pages/Bookkeeping1099';
import ExportToExcel from './pages/ExportToExcel';
import Comparison from './pages/Comparison';
import Invoicing from './pages/Invoicing';
import ScannerLanding from './pages/ScannerLanding';
import StateTaxLanding from './pages/StateTaxLanding';
import RegionTaxLanding from './pages/RegionTaxLanding';
import GccTaxLanding from './pages/GccTaxLanding';
import EsLanding from './pages/EsLanding';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import Refund from './pages/Refund';
import DataProtection from './pages/DataProtection';
import DeleteAccount from './pages/DeleteAccount';
import NotFound from './pages/NotFound';
// Lazy: authenticated app pages (heavy libs: recharts, xlsx, jspdf, plaid) — split out of the initial bundle
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Bills = lazy(() => import('./pages/Bills'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const TaxSummary = lazy(() => import('./pages/TaxSummary'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const WhatsAppConnect = lazy(() => import('./pages/WhatsAppConnect'));
const TaxPlanner = lazy(() => import('./pages/TaxPlanner'));
const FinancialStatements = lazy(() => import('./pages/FinancialStatements'));
const FilingCenter = lazy(() => import('./pages/FilingCenter'));
const ScanCenter = lazy(() => import('./pages/ScanCenter'));
const ReceiptsHub = lazy(() => import('./pages/ReceiptsHub'));
const ExportCenter = lazy(() => import('./pages/ExportCenter'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoicePublic = lazy(() => import('./pages/InvoicePublic'));

function App() {
  useEffect(() => {
    initGA();
    initNativeUI();
    initPushNotifications();
    checkForAppUpdate();
    captureReferral();
    initMetaAppEvents();
    markSession();
  }, []);

  return (
    <HelmetProvider>
    <ThemeProvider>
    <CountryProvider>
      <div className="App">
        <BrowserRouter>
          <RouteTracker />
          <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/menu" element={<Navigate to="/dashboard" replace />} />
            <Route path="/menu/:section" element={<Navigate to="/dashboard" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/welcome" element={<RequireAuth><Welcome /></RequireAuth>} />
            <Route path="/dashboard" element={<RequireAuth><AppShell><Dashboard /></AppShell></RequireAuth>} />
            <Route path="/transactions" element={<RequireAuth><AppShell><Transactions /></AppShell></RequireAuth>} />
            <Route path="/bills" element={<RequireAuth><AppShell><Bills /></AppShell></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><AppShell><Settings /></AppShell></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth><AppShell><AdminDashboard /></AppShell></RequireAuth>} />
            <Route path="/tax-summary" element={<RequireAuth><AppShell><TaxSummary /></AppShell></RequireAuth>} />
            <Route path="/tax-planner" element={<RequireAuth><AppShell><TaxPlanner /></AppShell></RequireAuth>} />
            <Route path="/financials" element={<RequireAuth><AppShell><FinancialStatements /></AppShell></RequireAuth>} />
            <Route path="/filing" element={<RequireAuth><AppShell><FilingCenter /></AppShell></RequireAuth>} />
            <Route path="/upgrade" element={<RequireAuth><Upgrade /></RequireAuth>} />
            <Route path="/payment-success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
            <Route path="/whatsapp" element={<RequireAuth><WhatsAppConnect /></RequireAuth>} />
            <Route path="/tools/1099-tax-calculator" element={<TaxCalculator initialRegion="US" />} />
            <Route path="/tools/uk-self-assessment-tax-calculator" element={<TaxCalculator initialRegion="UK" />} />
            <Route path="/tools/canada-self-employed-tax-calculator" element={<TaxCalculator initialRegion="CA" />} />
            <Route path="/tools/germany-self-employed-tax-calculator" element={<TaxCalculator initialRegion="DE" />} />
            <Route path="/tools/spain-autonomo-tax-calculator" element={<TaxCalculator initialRegion="ES" />} />
            <Route path="/tools/france-auto-entrepreneur-tax-calculator" element={<TaxCalculator initialRegion="FR" />} />
            <Route path="/tools/australia-sole-trader-tax-calculator" element={<TaxCalculator initialRegion="AU" />} />
            <Route path="/tools/new-zealand-sole-trader-tax-calculator" element={<TaxCalculator initialRegion="NZ" />} />
            <Route path="/tools/uae-vat-calculator" element={<TaxCalculator initialRegion="UAE" />} />
            <Route path="/tools/saudi-arabia-vat-calculator" element={<TaxCalculator initialRegion="KSA" />} />
            <Route path="/tools/australia-gst-calculator" element={<GstCalculator country="AU" />} />
            <Route path="/tools/new-zealand-gst-calculator" element={<GstCalculator country="NZ" />} />
            <Route path="/embed/tax-calculator" element={<EmbedCalculator />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/whatsapp-bookkeeping" element={<WhatsAppBookkeeping />} />
            <Route path="/whatsapp-accounting" element={<WhatsAppAccounting />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/business" element={<Business />} />
            <Route path="/invoice-scanner" element={<InvoiceScanner />} />
          <Route path="/scan-center" element={<ScanCenterLanding />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/receipt-scanning-for-taxes" element={<ReceiptScanningForTaxes />} />
        <Route path="/self-employed-bookkeeping" element={<SelfEmployedBookkeeping />} />
        <Route path="/whatsapp-bookkeeping-guide" element={<WhatsAppBookkeepingGuide />} />
            <Route path="/for-accountants" element={<ForAccountants />} />
            <Route path="/partners/accountants" element={<PartnersAccountants />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/app" element={<AppLink />} />
            <Route path="/about-moneyfeed" element={<AboutMoneyFeed />} />
            <Route path="/features" element={<Features />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/security" element={<Security />} />
            <Route path="/supported-countries" element={<SupportedCountries />} />
            <Route path="/tax-support" element={<TaxSupport />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/ai-bookkeeping" element={<AiBookkeeping />} />
            <Route path="/expense-tracking" element={<ExpenseTracking />} />
            <Route path="/mileage-tracking" element={<MileageTracking />} />
            <Route path="/freelancer-bookkeeping" element={<FreelancerBookkeeping />} />
            <Route path="/1099-bookkeeping" element={<Bookkeeping1099 />} />
            <Route path="/export-to-excel" element={<ExportToExcel />} />
            <Route path="/invoicing" element={<Invoicing />} />
            <Route path="/receipt-to-excel" element={<ScannerLanding slug="receipt-to-excel" />} />
            <Route path="/receipt-to-csv" element={<ScannerLanding slug="receipt-to-csv" />} />
            <Route path="/invoice-to-excel" element={<ScannerLanding slug="invoice-to-excel" />} />
            <Route path="/convert-pdf-invoice-to-excel" element={<ScannerLanding slug="convert-pdf-invoice-to-excel" />} />
            <Route path="/scan-receipts-to-pdf" element={<ScannerLanding slug="scan-receipts-to-pdf" />} />
            <Route path="/schedule-c-receipt-scanner" element={<ScannerLanding slug="schedule-c-receipt-scanner" />} />
            <Route path="/vat-receipt-scanner" element={<ScannerLanding slug="vat-receipt-scanner" />} />
            <Route path="/self-employment-tax-texas" element={<StateTaxLanding slug="self-employment-tax-texas" />} />
            <Route path="/self-employment-tax-florida" element={<StateTaxLanding slug="self-employment-tax-florida" />} />
            <Route path="/self-employment-tax-washington" element={<StateTaxLanding slug="self-employment-tax-washington" />} />
            <Route path="/self-employment-tax-california" element={<StateTaxLanding slug="self-employment-tax-california" />} />
            <Route path="/self-employment-tax-new-york" element={<StateTaxLanding slug="self-employment-tax-new-york" />} />
            <Route path="/self-employment-tax-illinois" element={<StateTaxLanding slug="self-employment-tax-illinois" />} />
            <Route path="/self-employment-tax-georgia" element={<StateTaxLanding slug="self-employment-tax-georgia" />} />
            <Route path="/self-employment-tax-pennsylvania" element={<StateTaxLanding slug="self-employment-tax-pennsylvania" />} />
        <Route path="/self-employment-tax-ohio" element={<StateTaxLanding slug="self-employment-tax-ohio" />} />
        <Route path="/self-employment-tax-north-carolina" element={<StateTaxLanding slug="self-employment-tax-north-carolina" />} />
        <Route path="/self-employment-tax-michigan" element={<StateTaxLanding slug="self-employment-tax-michigan" />} />
        <Route path="/self-employment-tax-new-jersey" element={<StateTaxLanding slug="self-employment-tax-new-jersey" />} />
        <Route path="/self-employment-tax-virginia" element={<StateTaxLanding slug="self-employment-tax-virginia" />} />
        <Route path="/self-employment-tax-arizona" element={<StateTaxLanding slug="self-employment-tax-arizona" />} />
        <Route path="/self-employment-tax-massachusetts" element={<StateTaxLanding slug="self-employment-tax-massachusetts" />} />
        <Route path="/self-employment-tax-colorado" element={<StateTaxLanding slug="self-employment-tax-colorado" />} />
        <Route path="/self-employment-tax-tennessee" element={<StateTaxLanding slug="self-employment-tax-tennessee" />} />
        <Route path="/self-employment-tax-indiana" element={<StateTaxLanding slug="self-employment-tax-indiana" />} />
        <Route path="/self-employment-tax-maryland" element={<StateTaxLanding slug="self-employment-tax-maryland" />} />
        <Route path="/self-employment-tax-wisconsin" element={<StateTaxLanding slug="self-employment-tax-wisconsin" />} />
        <Route path="/self-employment-tax-missouri" element={<StateTaxLanding slug="self-employment-tax-missouri" />} />
        <Route path="/self-employment-tax-minnesota" element={<StateTaxLanding slug="self-employment-tax-minnesota" />} />
        <Route path="/self-employment-tax-alabama" element={<StateTaxLanding slug="self-employment-tax-alabama" />} />
        <Route path="/self-employment-tax-south-carolina" element={<StateTaxLanding slug="self-employment-tax-south-carolina" />} />
        <Route path="/self-employment-tax-oregon" element={<StateTaxLanding slug="self-employment-tax-oregon" />} />
        <Route path="/self-employment-tax-louisiana" element={<StateTaxLanding slug="self-employment-tax-louisiana" />} />
        <Route path="/self-employment-tax-oklahoma" element={<StateTaxLanding slug="self-employment-tax-oklahoma" />} />
        <Route path="/self-employment-tax-utah" element={<StateTaxLanding slug="self-employment-tax-utah" />} />
      <Route path="/self-employment-tax-alaska" element={<StateTaxLanding slug="self-employment-tax-alaska" />} />
      <Route path="/self-employment-tax-arkansas" element={<StateTaxLanding slug="self-employment-tax-arkansas" />} />
      <Route path="/self-employment-tax-connecticut" element={<StateTaxLanding slug="self-employment-tax-connecticut" />} />
      <Route path="/self-employment-tax-delaware" element={<StateTaxLanding slug="self-employment-tax-delaware" />} />
      <Route path="/self-employment-tax-hawaii" element={<StateTaxLanding slug="self-employment-tax-hawaii" />} />
      <Route path="/self-employment-tax-idaho" element={<StateTaxLanding slug="self-employment-tax-idaho" />} />
      <Route path="/self-employment-tax-iowa" element={<StateTaxLanding slug="self-employment-tax-iowa" />} />
      <Route path="/self-employment-tax-kansas" element={<StateTaxLanding slug="self-employment-tax-kansas" />} />
      <Route path="/self-employment-tax-kentucky" element={<StateTaxLanding slug="self-employment-tax-kentucky" />} />
      <Route path="/self-employment-tax-maine" element={<StateTaxLanding slug="self-employment-tax-maine" />} />
      <Route path="/self-employment-tax-mississippi" element={<StateTaxLanding slug="self-employment-tax-mississippi" />} />
      <Route path="/self-employment-tax-montana" element={<StateTaxLanding slug="self-employment-tax-montana" />} />
      <Route path="/self-employment-tax-nebraska" element={<StateTaxLanding slug="self-employment-tax-nebraska" />} />
      <Route path="/self-employment-tax-nevada" element={<StateTaxLanding slug="self-employment-tax-nevada" />} />
      <Route path="/self-employment-tax-new-hampshire" element={<StateTaxLanding slug="self-employment-tax-new-hampshire" />} />
      <Route path="/self-employment-tax-new-mexico" element={<StateTaxLanding slug="self-employment-tax-new-mexico" />} />
      <Route path="/self-employment-tax-north-dakota" element={<StateTaxLanding slug="self-employment-tax-north-dakota" />} />
      <Route path="/self-employment-tax-rhode-island" element={<StateTaxLanding slug="self-employment-tax-rhode-island" />} />
      <Route path="/self-employment-tax-south-dakota" element={<StateTaxLanding slug="self-employment-tax-south-dakota" />} />
      <Route path="/self-employment-tax-vermont" element={<StateTaxLanding slug="self-employment-tax-vermont" />} />
      <Route path="/self-employment-tax-west-virginia" element={<StateTaxLanding slug="self-employment-tax-west-virginia" />} />
      <Route path="/self-employment-tax-wyoming" element={<StateTaxLanding slug="self-employment-tax-wyoming" />} />
            <Route path="/self-employed-tax-scotland" element={<RegionTaxLanding slug="self-employed-tax-scotland" />} />
            <Route path="/self-employed-tax-ireland" element={<RegionTaxLanding slug="self-employed-tax-ireland" />} />
            <Route path="/freelancer-tax-germany" element={<RegionTaxLanding slug="freelancer-tax-germany" />} />
            <Route path="/zzp-tax-netherlands" element={<RegionTaxLanding slug="zzp-tax-netherlands" />} />
            <Route path="/autonomo-tax-spain" element={<RegionTaxLanding slug="autonomo-tax-spain" />} />
            <Route path="/auto-entrepreneur-tax-france" element={<RegionTaxLanding slug="auto-entrepreneur-tax-france" />} />
            <Route path="/freelancer-tax-italy" element={<RegionTaxLanding slug="freelancer-tax-italy" />} />
            <Route path="/freelancer-tax-portugal" element={<RegionTaxLanding slug="freelancer-tax-portugal" />} />
            <Route path="/self-employed-tax-belgium" element={<RegionTaxLanding slug="self-employed-tax-belgium" />} />
            <Route path="/freelancer-tax-switzerland" element={<RegionTaxLanding slug="freelancer-tax-switzerland" />} />
            <Route path="/self-employed-tax-poland" element={<RegionTaxLanding slug="self-employed-tax-poland" />} />
            <Route path="/self-employed-tax-australia" element={<RegionTaxLanding slug="self-employed-tax-australia" />} />
            <Route path="/self-employed-tax-new-zealand" element={<RegionTaxLanding slug="self-employed-tax-new-zealand" />} />
            <Route path="/vat-freelancers-uae" element={<GccTaxLanding slug="vat-freelancers-uae" />} />
            <Route path="/vat-freelancers-saudi-arabia" element={<GccTaxLanding slug="vat-freelancers-saudi-arabia" />} />
            <Route path="/uae-e-invoicing" element={<GccTaxLanding slug="uae-e-invoicing" />} />
            <Route path="/zatca-e-invoicing-saudi-arabia" element={<GccTaxLanding slug="zatca-e-invoicing-saudi-arabia" />} />
            <Route path="/ar/vat-freelancers-uae" element={<GccTaxLanding slug="vat-freelancers-uae" lang="ar" />} />
            <Route path="/ar/vat-freelancers-saudi-arabia" element={<GccTaxLanding slug="vat-freelancers-saudi-arabia" lang="ar" />} />
            <Route path="/ar/uae-e-invoicing" element={<GccTaxLanding slug="uae-e-invoicing" lang="ar" />} />
            <Route path="/ar/zatca-e-invoicing-saudi-arabia" element={<GccTaxLanding slug="zatca-e-invoicing-saudi-arabia" lang="ar" />} />
            <Route path="/es/calculadora-impuestos-1099" element={<EsLanding slug="calculadora-impuestos-1099" />} />
            <Route path="/es/contabilidad-por-whatsapp" element={<EsLanding slug="contabilidad-por-whatsapp" />} />
            <Route path="/es/impuestos-trabajador-independiente-usa" element={<EsLanding slug="impuestos-trabajador-independiente-usa" />} />
            <Route path="/es/escanear-recibos-a-excel" element={<EsLanding slug="escanear-recibos-a-excel" />} />
            <Route path="/es/escanear-recibos-a-pdf" element={<EsLanding slug="escanear-recibos-a-pdf" />} />
            <Route path="/es/deducciones-impuestos-trabajador-independiente-usa" element={<EsLanding slug="deducciones-impuestos-trabajador-independiente-usa" />} />
            <Route path="/es/impuestos-conductores-repartidores-usa" element={<EsLanding slug="impuestos-conductores-repartidores-usa" />} />
            <Route path="/moneyfeed-vs-quickbooks" element={<Comparison slug="quickbooks" />} />
            <Route path="/moneyfeed-vs-freshbooks" element={<Comparison slug="freshbooks" />} />
            <Route path="/moneyfeed-vs-keeper" element={<Comparison slug="keeper" />} />
            <Route path="/scan-center" element={<RequireAuth><AppShell><ScanCenter /></AppShell></RequireAuth>} />
            <Route path="/receipts" element={<RequireAuth><AppShell><ReceiptsHub /></AppShell></RequireAuth>} />
            <Route path="/export-center" element={<RequireAuth><AppShell><ExportCenter /></AppShell></RequireAuth>} />
            <Route path="/invoices" element={<RequireAuth><AppShell><Invoices /></AppShell></RequireAuth>} />
            <Route path="/invoice/:token" element={<InvoicePublic />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/support" element={<Support />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/data-protection" element={<DataProtection />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <InstallPrompt />
        <UpdatePrompt />
        <ShareAppPrompt />
        <Toaster position="top-right" richColors />
      </div>
    </CountryProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
